import { postReviewError } from 'features/reviews/reducers/post-reviews';
import React from 'react';
import { Breakpoint } from 'types/utils';
import { PostReviewFieldError } from 'features/reviews';
import { RegionsStoresResponse } from 'features/bisiness-units/types';
import type { Action, ActionType } from 'typesafe-actions';
import { useSelector } from 'react-redux';
import { ReviewFormSection } from '../review-form';
import { setAuthorDetailShop, FormState, formState } from '../state';
import { ReviewFormFieldError, ReviewFormProps } from '../types';
import { ErrorKeys, MessageKeys } from '../validators';
import { buildInitState } from './build-init-state';
import { postSubmitErrorToArray } from './submit-errors-utils';
import { mapDispatchToProps, mapStoreToProps } from './state-mappers';
import { photosUploadHasError } from '../../reviews/selectors';
import i18next from 'i18next';
import { useErrorsNotifications } from './review-form-error-notification';

export type ReviewFormStatefullOnSubmit = (formState: FormState) => void;

export type ReviewFormStatefullProps = {
  availabledStores: RegionsStoresResponse['availabledStores'] | null;
  availabledRegions: RegionsStoresResponse['availabledRegions'] | null;
  breakpoint: Breakpoint;
  postSubmitErrors: ReturnType<typeof postReviewError>;
  submitIsPerforming: boolean;
  onSubmit?: ReviewFormStatefullOnSubmit;
  onReviewPublicationErrorShown?: (params: { ruText: string }) => void;
} & Pick<
  ReviewFormProps,
  | 'productTile'
  | 'selectedPictures'
  | 'onPictureRetryClick'
  | 'onPictureRemoveClick'
  | 'onPictureSelectionClick'
  | 'onPicturesFilesSelect'
  | 'onBodyFieldFocus'
  | 'onProsFieldFocus'
  | 'onConsFieldFocus'
  | 'setShop'
> & {
    dispatch?: <T extends Action>(payload: T) => void;
  } & Omit<React.HTMLAttributes<HTMLElement>, 'onSubmit'>;

export const ReviewFormStatefull: React.FC<ReviewFormStatefullProps> = ({
  breakpoint,
  availabledStores,
  availabledRegions,
  postSubmitErrors,
  onSubmit,
  onReviewPublicationErrorShown,
  dispatch: parentDispatch,
  setShop: parentSetShop,
  ...formProps
}) => {
  // do not display any error messages before first click on submit button
  const [isFirstShow, setFirstShow] = React.useState<boolean>(true);
  const [draftState, dispatch] = React.useReducer(formState, undefined, buildInitState);

  const [selectedRegion, setSelectedRegion] = React.useState<string | null>(null);
  const shopsByRegionMemoized = React.useMemo(() => {
    const regionId = availabledRegions?.find((reg) => reg.title === selectedRegion)?.id;
    return availabledStores?.filter((r) => r.regionId === regionId).map((r) => r.title) ?? null;
  }, [selectedRegion, availabledStores, availabledRegions]);

  const hasImageUploadError = useSelector(photosUploadHasError);

  const doSetShop = React.useCallback(
    doSetShopCb(
      (payload) => {
        dispatch(payload);
        parentDispatch?.(payload);
        parentSetShop(null, payload.payload);
      },
      (region) => {
        setSelectedRegion(region);
        parentSetShop(region, null);
      },
    ),
    [dispatch, parentDispatch, setSelectedRegion, parentSetShop],
  );

  const canDoSubmit = isFirstShow || draftState.fieldsErrors.length === 0;

  const performSubmit = React.useCallback(performSubmitCb(draftState, onSubmit), [draftState, onSubmit]);

  const doSubmit = React.useCallback(doSubmitCb(performSubmit, setFirstShow), [performSubmit, setFirstShow]);

  const errors = React.useMemo(
    () => mergePreAndPostSubmitErrors(draftState.fieldsErrors, postSubmitErrorToArray(postSubmitErrors)),
    [draftState.fieldsErrors, postSubmitErrors],
  );

  useErrorsNotifications(errors, isFirstShow, onReviewPublicationErrorShown);

  const dispatchProps = React.useMemo(
    () =>
      mapDispatchToProps((payload) => {
        dispatch(payload);
        parentDispatch?.(payload);
      }),
    [dispatch, parentDispatch],
  );

  const regions = React.useMemo(() => availabledRegions?.map((r) => r.title) ?? [], [availabledRegions]);

  return (
    <>
      <ReviewFormSection
        canDoSubmit={canDoSubmit}
        onSubmit={doSubmit}
        breakpoint={breakpoint}
        region={selectedRegion}
        availabledRegions={regions}
        availabledStores={shopsByRegionMemoized}
        setShop={doSetShop}
        toShowErrors={!isFirstShow}
        hasImageUploadError={hasImageUploadError}
        {...formProps}
        {...mapStoreToProps(draftState)}
        {...dispatchProps}
      />
    </>
  );
};

export const doSetShopCb =
  (
    dispatch: React.Dispatch<ActionType<typeof setAuthorDetailShop>>,
    setSelectedRegion: (region: string | null) => void,
  ) =>
  (region: string | null, shop: string | null) => {
    if (shop === null) {
      setSelectedRegion(region);
    }
    dispatch(setAuthorDetailShop(shop));
  };

const performSubmitCb = (draftState: FormState, onSubmit?: ReviewFormStatefullOnSubmit) => () => {
  if (draftState.fieldsErrors.length === 0) {
    onSubmit?.(draftState);
  }
};

const doSubmitCb =
  (performSubmit: () => void, setFirstShow: React.Dispatch<React.SetStateAction<boolean>>) =>
  (event: React.FormEvent<HTMLElement>) => {
    event.preventDefault();
    setFirstShow(false);
    performSubmit();
  };

export const mergePreAndPostSubmitErrors = (
  fieldsErrors: ReviewFormFieldError[],
  postSubmitErrors: PostReviewFieldError[],
) => {
  const result = [];

  // errors of pre-submit stage
  if (fieldsErrors.find((r) => r.errorKey === ErrorKeys.FIELDS_MISSING)) {
    result.push({
      errorKey: ErrorKeys.FIELDS_MISSING,
      message: i18next.t(MessageKeys.FILL_REQUIRED_FIELDS),
      fieldKey: '',
    });
  }
  if (fieldsErrors.find((r) => r.errorKey === ErrorKeys.MAX_LENGTH_EXCEEDED)) {
    result.push({
      errorKey: ErrorKeys.MAX_LENGTH_EXCEEDED,
      message: i18next.t(MessageKeys.MAX_TEXT_CONTENT_IS_1500),
      fieldKey: '',
    });
  }

  // errors of post-submit stage
  result.push(...postSubmitErrors);

  return result;
};
